import { useState } from "react";
import { ChevronRight, Zap, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import { endpoints } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// -----------------------------------------------------------------------------
// DexDeviceRow — one row in the DEX risk cohort list.
//
// Used by:
//   • <DexRiskDrawer> on /admin/cockpit (opened from attention band)
//   • <DexRiskCohort> inline panel on /lifecycle?stage=operate
//
// Collapsed state: single-line summary (id · user · model · DEX pill · issue).
// Expanded state (click row): soft brand-tinted mini detail with:
//   • 30d DEX sparkline (inline SVG polyline, no library)
//   • 3-row incident timeline
//   • Two action buttons → fire real backend endpoints → success chip inline
// -----------------------------------------------------------------------------

const SEV_STYLES = {
  red:   { bg: "#FEE2E2", fg: "#B91C1C", stroke: "#DC2626" },
  amber: { bg: "#FEF3C7", fg: "#B45309", stroke: "#D97706" },
  green: { bg: "#DCFCE7", fg: "#15803D", stroke: "#16A34A" },
};

function Sparkline({ series, strokeColor }) {
  if (!series || series.length === 0) return null;
  const w = 90;
  const h = 28;
  const min = Math.min(...series);
  const max = Math.max(...series);
  const range = Math.max(1, max - min);
  const points = series
    .map((v, i) => {
      const x = (i / (series.length - 1)) * w;
      const y = h - ((v - min) / range) * h;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(" ");
  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="shrink-0" aria-hidden>
      <polyline
        fill="none"
        stroke={strokeColor}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={points}
      />
    </svg>
  );
}

export function DexDeviceRow({ device, expanded, onToggle, brandBlue, brandSoft }) {
  const [action, setAction] = useState({ loading: null, result: null });
  const sev = SEV_STYLES[device.severity] || SEV_STYLES.amber;

  const runAction = async (kind) => {
    setAction({ loading: kind, result: null });
    const payload = {
      device_id: device.device_id,
      assigned_to: device.assigned_to,
      top_issue: device.top_issue,
      suggested_action: device.suggested_action,
    };
    try {
      const res = kind === "self_heal"
        ? await endpoints.lifecycleDexSelfHeal(payload)
        : await endpoints.lifecycleDexProactiveSwap(payload);
      setAction({ loading: null, result: res });
      toast.success(res.message || "Action queued");
    } catch (e) {
      setAction({ loading: null, result: null });
      toast.error("Action failed");
    }
  };

  return (
    <div
      className="rounded-lg border border-slate-200 bg-white overflow-hidden transition-shadow"
      style={expanded ? { boxShadow: `0 1px 0 ${brandSoft}, 0 0 0 1px ${brandBlue}22` } : {}}
      data-testid={`dex-row-${device.device_id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-3 py-2.5 flex items-center gap-3 hover:bg-slate-50"
      >
        <ChevronRight
          size={13}
          className="text-slate-400 shrink-0 transition-transform"
          style={{ transform: expanded ? "rotate(90deg)" : "rotate(0)" }}
        />
        <div className="min-w-0 flex-1 grid grid-cols-12 gap-2 items-center">
          <div className="col-span-3 min-w-0">
            <div className="text-[12px] font-semibold text-slate-900 truncate">{device.device_id}</div>
            <div className="text-[10px] text-slate-500 truncate">{device.assigned_to}</div>
          </div>
          <div className="col-span-3 text-[11px] text-slate-600 truncate">{device.model}</div>
          <div className="col-span-2 flex items-center">
            <span
              className="text-[11px] font-bold px-1.5 py-0.5 rounded"
              style={{ backgroundColor: sev.bg, color: sev.fg }}
              data-testid={`dex-pill-${device.device_id}`}
            >
              DEX {device.dex_score}
            </span>
          </div>
          <div className="col-span-4 text-[10.5px] text-slate-600 truncate">
            <span className="font-semibold text-slate-700">{device.top_issue}</span>
            <span className="text-slate-400"> · </span>
            <span>{device.suggested_action}</span>
          </div>
        </div>
      </button>

      {expanded && (
        <div
          className="px-4 py-3 border-t"
          style={{ backgroundColor: brandSoft, borderColor: `${brandBlue}22` }}
          data-testid={`dex-detail-${device.device_id}`}
        >
          <div className="flex items-start gap-4 flex-wrap mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                30d DEX trend
              </div>
              <div className="flex items-center gap-2">
                <Sparkline series={device.trend_30d} strokeColor={sev.stroke} />
                <div className="text-[11px] text-slate-600">
                  <span className="font-semibold" style={{ color: sev.fg }}>DEX {device.dex_score}</span>
                  <span className="text-slate-400"> · </span>
                  <span>
                    {device.trend_30d[0]} → {device.trend_30d[device.trend_30d.length - 1]}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                Incident timeline
              </div>
              <div className="space-y-0.5">
                {(device.incidents || []).map((it, i) => (
                  <div key={i} className="text-[11px] text-slate-700 flex gap-2">
                    <span className="text-slate-400 font-mono shrink-0">{it.at}</span>
                    <span className="truncate">{it.event}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {action.result ? (
            <div
              className="rounded-md px-3 py-2 flex items-center gap-2 text-[11.5px] font-semibold"
              style={{ backgroundColor: "#ECFDF5", color: "#0F766E" }}
              data-testid={`dex-action-success-${device.device_id}`}
            >
              <CheckCircle2 size={13} />
              {action.result.message}
              <span className="ml-auto text-[10px] font-mono bg-white/60 px-1.5 py-0.5 rounded">
                {action.result.sn_number}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                data-testid={`dex-self-heal-btn-${device.device_id}`}
                onClick={() => runAction("self_heal")}
                disabled={!!action.loading}
                className="h-8 text-xs text-white hover:opacity-90"
                style={{ backgroundColor: brandBlue }}
              >
                {action.loading === "self_heal" ? (
                  <Loader2 size={11} className="mr-1.5 animate-spin" />
                ) : (
                  <Zap size={11} className="mr-1.5" />
                )}
                Trigger self-heal
              </Button>
              <Button
                data-testid={`dex-swap-btn-${device.device_id}`}
                onClick={() => runAction("proactive_swap")}
                disabled={!!action.loading}
                variant="outline"
                className="h-8 text-xs border-slate-300"
              >
                {action.loading === "proactive_swap" ? (
                  <Loader2 size={11} className="mr-1.5 animate-spin" />
                ) : (
                  <RefreshCw size={11} className="mr-1.5" style={{ color: brandBlue }} />
                )}
                Proactive swap
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
